// Blog / Insights seed content.
// Keep posts as data (no CMS yet). Each `body` is an array of paragraphs OR
// tuples of ["h2", "heading"] / ["ul", ["a", "b"]] / ["p", "text"].

export const POSTS = [
  {
    slug: "brighter-smile-6-daily-habits",
    title: "6 Everyday Habits for a Brighter Smile",
    excerpt:
      "Small daily choices — the order you brush, how you sip coffee, when you floss — quietly shape the color and health of your smile. Here's what actually matters.",
    cover: "/site-assets/office-home1.webp",
    author: "Dr. Serena Ide Noumy",
    tags: ["Hygiene", "Preventive"],
    publishedAt: "2026-02-14",
    readMinutes: 4,
    body: [
      ["p", "You don't need a shelf full of products to keep your smile bright. A few small, repeatable habits do more than any single whitening treatment ever could. These are the six we recommend most often at Smiles of Loudoun."],
      ["h2", "1. Brush before breakfast, not after"],
      ["p", "Acidic breakfasts (orange juice, coffee, fruit) soften enamel for about 30 minutes. Brushing on softened enamel wears it down. Brush first, then enjoy breakfast."],
      ["h2", "2. Floss before you brush"],
      ["p", "Flossing first loosens plaque so fluoride toothpaste can reach between teeth. It also means you're less likely to skip flossing when you're tired."],
      ["h2", "3. Sip dark drinks through a straw"],
      ["p", "Coffee, tea, and red wine stain the front of your teeth first because that's what contacts them. A straw routes the liquid past your smile line."],
      ["h2", "4. Rinse with water after acidic meals"],
      ["p", "Swishing water for 10 seconds after acidic foods or wine neutralizes acid quickly and prevents staining. It's the easiest habit on this list."],
      ["h2", "5. Change your brush head every 8–12 weeks"],
      ["p", "Frayed bristles clean about 40% less effectively. If you can see the bristles splaying outward, it's time."],
      ["h2", "6. Book your cleaning every six months"],
      ["p", "The single biggest predictor of a bright smile at 60 is showing up for regular cleanings at 30. If it's been more than six months, we'd love to see you."],
    ],
  },
  {
    slug: "invisalign-vs-braces",
    title: "Invisalign vs. Traditional Braces — Which Is Right for You?",
    excerpt:
      "Both work. The right choice depends on your bite, your lifestyle, and how much of the process you want to be visible.",
    cover: "/site-assets/invisalign-pic.webp",
    author: "Dr. Steve Yopa",
    tags: ["Cosmetic", "Invisalign"],
    publishedAt: "2026-02-01",
    readMinutes: 5,
    body: [
      ["p", "\"Which is better — Invisalign or braces?\" is the question we hear most in our cosmetic consults. The honest answer is: it depends. Here's how we help patients decide."],
      ["h2", "When Invisalign is the better fit"],
      ["ul", [
        "You're an adult or teen with mild to moderate crowding",
        "You want to eat and drink normally without food restrictions",
        "You'd rather no one know you're straightening your teeth",
        "You're disciplined about wearing aligners 22 hours a day",
      ]],
      ["h2", "When traditional braces make sense"],
      ["ul", [
        "Complex bite issues requiring precise tooth-by-tooth control",
        "Younger patients whose compliance is a concern",
        "Rotations of premolars and canines that aligners struggle with",
      ]],
      ["h2", "Cost and timeline"],
      ["p", "In our practice, Invisalign and braces tend to run within a few hundred dollars of each other. Treatment length averages 12–18 months for both, though minor Invisalign cases can finish in as little as 6 months."],
      ["h2", "What we recommend"],
      ["p", "Come in for a complimentary consult. We'll take a quick 3D scan (no goopy impressions) and show you both treatment plans side by side. No pressure — just clarity."],
    ],
  },
  {
    slug: "signs-you-might-grind-teeth",
    title: "5 Quiet Signs You Might Be Grinding Your Teeth",
    excerpt:
      "Bruxism rarely wakes the person doing it. Look for these five clues before your enamel does.",
    cover: "/site-assets/office-home2.webp",
    author: "Dr. Serena Ide Noumy",
    tags: ["Preventive", "TMJ"],
    publishedAt: "2026-01-18",
    readMinutes: 3,
    body: [
      ["p", "About one in three adults grinds their teeth at night, and almost none of them know it. Here are the five clues we look for during your six-month cleaning."],
      ["h2", "1. Morning jaw soreness"],
      ["p", "A tender, tired feeling in your jaw when you first wake up is the most reliable sign that you've been clenching or grinding overnight."],
      ["h2", "2. Flattened or chipped front teeth"],
      ["p", "Look at your upper and lower front teeth in a mirror. If the edges look worn to a flat, matching line, that's bruxism wearing them down."],
      ["h2", "3. Sensitivity to cold air"],
      ["p", "Grinding thins enamel and exposes dentin, which is very sensitive to temperature changes."],
      ["h2", "4. Headaches at your temples"],
      ["p", "The muscles that clench your jaw connect to your temples. Chronic tension there is often bruxism, not sinus issues."],
      ["h2", "5. Your partner has heard grinding sounds"],
      ["p", "If someone has told you they hear you grind, believe them. Most patients need to hear it from another person before they take it seriously."],
      ["h2", "The fix is simple"],
      ["p", "A custom night guard costs a fraction of the crowns and veneers required to repair worn teeth later. If any of the above sounds familiar, ask about a night guard at your next visit."],
    ],
  },
  {
    slug: "insurance-vs-membership-plan",
    title: "Dental Insurance vs. Our Membership Plan — Which Saves More?",
    excerpt:
      "If you're paying for coverage you barely use, a simple membership plan may leave more in your pocket. Here's a plain-English comparison.",
    cover: "/site-assets/office-home3.webp",
    author: "Dr. Salar Ebrahimi",
    tags: ["Finance", "Patient Resources"],
    publishedAt: "2026-01-04",
    readMinutes: 4,
    body: [
      ["p", "Traditional dental insurance can be a great deal — or a puzzle of deductibles, waiting periods, and yearly maximums. Below is the plain-English comparison we walk new patients through."],
      ["h2", "Traditional PPO insurance"],
      ["ul", [
        "Monthly premium: usually $30–$80/mo per person",
        "Covers 100% of preventive cleanings & exams",
        "Covers 50–80% of restorative work — after deductibles",
        "Annual maximum benefit: usually $1,500 (unchanged for 40 years)",
      ]],
      ["h2", "Our in-house membership plan"],
      ["ul", [
        "Flat annual fee (no deductible, no maximum)",
        "Two cleanings + exam + X-rays included",
        "20% off all other services",
        "No claims to file, no waiting periods",
      ]],
      ["h2", "Which one wins?"],
      ["p", "If you have employer-provided PPO coverage, keep it — it's usually a subsidized benefit. If you're paying out of pocket for individual dental insurance and rarely need more than cleanings, the membership plan often saves families a few hundred dollars a year."],
      ["h2", "Not sure?"],
      ["p", "Bring your insurance card to your first visit. We'll do a real cost comparison based on the treatment you actually need — not what a chart says you should need."],
    ],
  },
  {
    slug: "whitening-safety-what-actually-works",
    title: "Teeth Whitening Safety — What Actually Works (and What Wrecks Your Enamel)",
    excerpt:
      "Charcoal, baking soda, over-the-counter strips, in-office bleaching — here's the honest ranking, side effects and all, from a dentist who does this every day.",
    cover: "/site-assets/office-home4.webp",
    author: "Dr. Steve Yopa",
    tags: ["Cosmetic", "Preventive"],
    publishedAt: "2026-02-28",
    readMinutes: 5,
    body: [
      ["p", "Every week a patient asks me about a whitening product they saw on TikTok. Some are harmless. Some quietly wreck enamel that took a lifetime to grow. Here's the plain-English safety ranking we use with our own family."],
      ["h2", "1. In-office professional whitening — safest, fastest"],
      ["p", "A dentist isolates your gums, applies a high-percentage hydrogen peroxide gel, and activates it for controlled 15-minute intervals. Enamel is unharmed because dwell time and concentration are supervised. Results are 4–8 shades brighter in a single visit."],
      ["h2", "2. Custom take-home trays — safest at-home option"],
      ["p", "We take an impression, make trays that hug your teeth, and send you home with medical-grade gel. It's the same active ingredient as in-office whitening, just at a lower concentration you wear overnight. Enamel-safe, and results build gradually over 10–14 days."],
      ["h2", "3. Over-the-counter whitening strips — usually fine"],
      ["p", "Reputable-brand strips (Crest 3D White, etc.) use lower peroxide concentrations that are safe when used as directed. The catch: they don't hug the curves of your teeth, so you'll see uneven whitening, and up to 60% of users report short-term sensitivity."],
      ["h2", "4. Whitening toothpastes — mostly abrasion, not whitening"],
      ["p", "Most whitening toothpastes work by scrubbing surface stains off, not chemically brightening the tooth. Fine in moderation — but daily use of the abrasive ones over years measurably wears enamel."],
      ["h2", "5. Charcoal toothpaste — no."],
      ["p", "Activated charcoal is highly abrasive and there is zero peer-reviewed evidence it whitens teeth. What it does do is scratch enamel microscopically, making teeth more prone to staining long-term. Skip it."],
      ["h2", "6. Baking soda + hydrogen peroxide DIY — hard pass"],
      ["p", "Both ingredients are legitimately used in dentistry — but at controlled concentrations and pH. The DIY mix people find online is usually too abrasive, too acidic, or both. We've seen chemical burns on gums from this."],
      ["h2", "The bigger safety issue nobody talks about"],
      ["p", "Sensitivity is normal for 24–48 hours after whitening. Long-lasting sensitivity means the concentration was too high or dwell time too long — which is why we don't recommend DIY \"stronger\" gels sold online. A little patience with a supervised protocol beats scrambling to fix damaged enamel later."],
      ["h2", "What we recommend"],
      ["p", "Come in for a shade check. Depending on your teeth, we'll suggest either in-office (fastest) or custom trays (most economical). Neither costs as much as most patients assume, and both are dramatically safer than what's under $40 online."],
    ],
  },
  {
    slug: "kids-first-dental-visit-what-to-expect",
    title: "Your Child's First Dental Visit — What to Expect (And How to Make It Fun)",
    excerpt:
      "The first visit isn't about drills or scary chairs — it's about building trust for the next 80 years of smiles. Here's how we make it feel like an adventure.",
    cover: "/site-assets/office-home5.webp",
    author: "Dr. Serena Ide Noumy",
    tags: ["Pediatric", "Family"],
    publishedAt: "2026-03-14",
    readMinutes: 4,
    body: [
      ["p", "The American Academy of Pediatric Dentistry recommends a first dental visit by age 1, or within six months of the first tooth erupting. That surprises most new parents — but the earlier we build a positive relationship, the calmer every visit for the next 20 years."],
      ["h2", "When to schedule the first visit"],
      ["ul", [
        "By your child's first birthday, or within 6 months of the first tooth",
        "Book a morning appointment when they're well-rested",
        "Try to schedule after nap or before hungry-cranky hours",
      ]],
      ["h2", "What actually happens at the first visit"],
      ["p", "The first visit is short — about 20 minutes — and gentle. We call it a \"happy visit.\" Here's the flow:"],
      ["ul", [
        "Meet the team and tour the room (the chair goes up and down — kids love this)",
        "Count teeth together in a fun, playful way",
        "Quick knee-to-knee exam with the parent holding the child",
        "Gentle polish with a soft brush and a fluoride varnish",
        "Take-home goodie bag and a new toothbrush",
      ]],
      ["h2", "How to prep your child (and yourself)"],
      ["ul", [
        "Read a friendly dentist book beforehand (Peppa Pig has a great one)",
        "Avoid loaded words like \"shot,\" \"drill,\" \"pain,\" or \"hurt\" — even to reassure",
        "Practice \"opening wide\" with them at home like it's a game",
        "Bring their favorite stuffed animal — we'll examine its teeth too",
        "Stay calm yourself. Kids read your energy first, our tools second",
      ]],
      ["h2", "What we teach parents"],
      ["p", "By the end of the first visit, you'll know how to properly brush little teeth, when to start flossing, how much fluoride toothpaste is right for their age (hint: a smear until age 3, a pea-sized amount after), and which snacks silently cause cavities."],
      ["h2", "What if they cry?"],
      ["p", "About one in three toddlers cries at some point. That's completely fine and expected. We never force a treatment on a distressed child at a first visit — the goal is a positive memory, not a completed cleaning. Sometimes a happy visit is just sitting in the chair with a big-kid sticker at the end. That still counts as a win."],
      ["h2", "Ready to book?"],
      ["p", "Call us at 571-510-3034 — mention it's your child's first visit and we'll block a longer slot so nobody feels rushed. Our team is bilingual (English, Spanish, French, and Farsi) and we love welcoming new little smiles."],
    ],
  },
];

export function findPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}
